// src/components/AppLayout.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { filterNavByPermissions } from "../config/permissions";
import { PREDEFINED_ROLES } from "../config/roles";
import { useRoles } from "../hooks/useRoles";
import { useSubscription } from "../hooks/useSubscription";
import SubscriptionBanner from "./SubscriptionBanner";
import SubscriptionExpiredScreen from "./SubscriptionExpiredScreen";
import sahodIcon from "../assets/brand/sahod-icon-light.svg";
import Icon from "./icons/Icon";
import NotificationBell from "./notifications/NotificationBell";
import { Wordmark } from "./Wordmark";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import "../styles/components/header-sidenav.css";

export const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, logout, hasPermission } = useAuth();
  const { resolveRoleName } = useRoles(company?.id);
  // Non-superusers on an expired subscription get a one-page block (logged in;
  // top header + sign-out stay, side nav dropped). Superusers fall through to the
  // normal shell with the read-only clamp (applied in useAuth) already in effect.
  const { isBlocked } = useSubscription();

  // State for mobile menu and dropdown
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [otPendingCount, setOtPendingCount] = useState(0);
  const [payrollPendingCount, setPayrollPendingCount] = useState(0);
  const [kioskAwaitingCount, setKioskAwaitingCount] = useState(0);

  // Refs for click outside detection
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Add body scroll lock
    if (!isMobileMenuOpen) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle logout with dropdown close
  const handleSignOut = () => {
    setIsDropdownOpen(false);
    logout();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleMobileClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest(".mobile-menu-btn")
      ) {
        setIsMobileMenuOpen(false);
        document.body.classList.remove("mobile-menu-open");
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleMobileClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleMobileClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on window resize (when screen gets larger)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        document.body.classList.remove("mobile-menu-open");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Real-time listener for OT approval pending count (sidebar badge)
  useEffect(() => {
    if (!company?.id || !hasPermission('time:ot:view')) return;
    const ref = doc(db, "companies", company.id, "summaries", "otApproval");
    const unsubscribe = onSnapshot(ref, (snap) => {
      setOtPendingCount(snap.exists() ? (snap.data().pending || 0) : 0);
    }, () => {
      // Silently ignore errors (e.g., permissions not yet set)
    });
    return unsubscribe;
  }, [company?.id, hasPermission]);

  // Real-time listener for payroll approval pending count (sidebar badge)
  useEffect(() => {
    if (!company?.id || !hasPermission('payroll:approve')) return;
    const ref = doc(db, "companies", company.id, "summaries", "payroll");
    const unsubscribe = onSnapshot(ref, (snap) => {
      setPayrollPendingCount(snap.exists() ? (snap.data().pendingApproval || 0) : 0);
    }, () => {
      // Silently ignore errors (e.g., permissions not yet set)
    });
    return unsubscribe;
  }, [company?.id, hasPermission]);

  // Real-time listener for kiosks awaiting pairing (sidebar badge). Reads the
  // count-only summary doc — kiosk docs themselves are deny-all to clients (C13).
  useEffect(() => {
    if (!company?.id || !hasPermission('kiosk:view')) return;
    const ref = doc(db, "companies", company.id, "summaries", "kiosks");
    const unsubscribe = onSnapshot(ref, (snap) => {
      setKioskAwaitingCount(snap.exists() ? (snap.data().awaiting || 0) : 0);
    }, () => {
      // Silently ignore errors (e.g., permissions not yet set)
    });
    return unsubscribe;
  }, [company?.id, hasPermission]);

  // Close mobile menu on navigation
  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    document.body.classList.remove("mobile-menu-open");
  };

  // Build filtered nav based on user permissions
  const filteredNav = useMemo(
    () => filterNavByPermissions(user?.userData?.permissions),
    [user?.userData?.permissions]
  );

  // Check if pathname contains a path segment (boundary-aware)
  // e.g. "/payroll" matches "/payroll" and "/payroll/process"
  //      but NOT "/settings/payroll-rates"
  const pathMatches = (pathname, path) => {
    const segment = `/${path}`;
    const idx = pathname.indexOf(segment);
    if (idx === -1) return false;
    const afterChar = pathname[idx + segment.length];
    return !afterChar || afterChar === '/' || afterChar === '?';
  };

  // Determine if a nav child is the active one among its siblings
  const isChildActive = (child, siblings) => {
    if (!pathMatches(location.pathname, child.path)) return false;
    // Among siblings, only the most specific (longest) match wins
    return !siblings.some(
      (s) =>
        s.id !== child.id &&
        s.path.length > child.path.length &&
        pathMatches(location.pathname, s.path)
    );
  };

  // For standalone items (no children)
  const isItemActive = (path) => {
    return pathMatches(location.pathname, path);
  };

  // Resolve display role label:
  // - Predefined role → its label from PREDEFINED_ROLES
  // - Custom role → the role's name from the live roles subcollection
  //   (renames reflect immediately via the useRoles onSnapshot listener;
  //   role reassignment propagates on next token refresh)
  const roleLabel = (() => {
    const role = user?.userData?.role;
    if (!role) return "User";
    if (role === "custom") return resolveRoleName(user?.userData?.customRoleId);
    return PREDEFINED_ROLES[role]?.label
      || role.charAt(0).toUpperCase() + role.slice(1);
  })();

  // Safety check - if user or company is not loaded, show loading state
  if (!user || !company) {
    return (
      <div className="page">
        <div className="loading">
          <div className="loading__spinner--ph"></div>
          <div className="loading__text">Loading...</div>
          <div className="loading__subtext">
            Please wait while we prepare everything
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay active"
          onClick={() => {
            setIsMobileMenuOpen(false);
            document.body.classList.remove("mobile-menu-open");
          }}
        ></div>
      )}

      {/* Sidebar Navigation — always rendered. For the non-superuser expired
          block, nav remains clickable (URL changes) but the content area below
          is swapped to <SubscriptionExpiredScreen/>, so every route resolves to
          the one-pager. */}
      <aside
        ref={mobileMenuRef}
        className={`sidebar ${isMobileMenuOpen ? "mobile-open" : ""}`}
      >
        {/* SaaS Application Header in Sidebar */}
        <div className="saas-header">
          <div className="saas-brand">
            <img className="saas-logo" src={sahodIcon} alt="" />
            <div className="saas-name">
              <h1><Wordmark /></h1>
            </div>
          </div>
        </div>

        {/* Navigation — data-driven from permissions config */}
        <div className="sidebar-nav">
          {filteredNav.map((item) => (
            <div className="nav-section" key={item.id}>
              {item.sectionTitle && (
                <div className="nav-title">{item.sectionTitle}</div>
              )}

              {/* Standalone item (no children) */}
              {item.path && !item.children && (
                <a
                  href={`/company/${company.slug}/${item.path}`}
                  className={`nav-item ${isItemActive(item.path) ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation(`/company/${company.slug}/${item.path}`);
                  }}
                >
                  {item.icon && <span className="nav-icon"><Icon name={item.icon} size={18} /></span>}
                  {item.label}
                </a>
              )}

              {/* Section with children */}
              {item.children && item.children.map((child) => (
                child.comingSoon ? (
                  <span key={child.id} className="nav-item disabled" title="Coming Soon">
                    {child.icon && <span className="nav-icon"><Icon name={child.icon} size={18} /></span>}
                    {child.label}
                    <span className="coming-soon-badge">Soon</span>
                  </span>
                ) : (
                  <a
                    key={child.id}
                    href={`/company/${company.slug}/${child.path}`}
                    className={`nav-item ${isChildActive(child, item.children) ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation(`/company/${company.slug}/${child.path}`);
                    }}
                  >
                    {child.icon && <span className="nav-icon"><Icon name={child.icon} size={18} /></span>}
                    {child.label}
                    {child.id === 'payroll-approval' && payrollPendingCount > 0 && (
                      <span className="ot-pending-badge">{payrollPendingCount}</span>
                    )}
                    {child.id === 'ot-approval' && otPendingCount > 0 && (
                      <span className="ot-pending-badge">{otPendingCount}</span>
                    )}
                    {child.id === 'kiosks' && kioskAwaitingCount > 0 && (
                      <span className="ot-pending-badge">{kioskAwaitingCount}</span>
                    )}
                  </a>
                )
              ))}

            </div>
          ))}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Top Header */}
        <header className="header">
          <div className="header-left">
            <button
              className={`mobile-menu-btn ${isMobileMenuOpen ? "active" : ""}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <div className="hamburger">
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
              </div>
            </button>

            <div className="header-brand">
              <div className="header-brand-logo">{company.name.charAt(0)}</div>
              <div className="header-brand-text">{company.name}</div>
            </div>
          </div>

          <div className="header-right">
            <NotificationBell />
            <div
              className={`user-menu ${isDropdownOpen ? "dropdown-open" : ""}`}
              ref={dropdownRef}
              onClick={toggleDropdown}
            >
              <div className="user-avatar">
                {user?.userData?.fullName
                  ? user.userData.fullName
                      .split(" ")
                      .map((n) => n.charAt(0))
                      .join("")
                  : "U"}
              </div>
              <div className="user-info">
                <div className="user-name" title={user?.userData?.fullName || "User"}>
                  {user?.userData?.fullName || "User"}
                </div>
                <div className="user-role" title={roleLabel}>{roleLabel}</div>
              </div>
              <span className="dropdown-arrow"><Icon name="chevron-down" size={14} /></span>

              <div
                className={`dropdown-menu ${
                  isDropdownOpen ? "dropdown-open" : ""
                }`}
              >
                <a
                  href="#"
                  className="dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDropdownOpen(false);
                  }}
                >
                  <span className="dropdown-icon"><Icon name="user" size={16} /></span>
                  Profile
                </a>
                <button
                  className="dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSignOut();
                  }}
                >
                  <span className="dropdown-icon"><Icon name="log-out" size={16} /></span>
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Subscription banner (trial/grace/expired) — hidden for the block screen */}
        {!isBlocked && <SubscriptionBanner />}

        {/* Content Area — payroll wizard breaks out wider (PRD_PayrollProcessUI.md D1/D2) */}
        <main
          className={`content-area${
            pathMatches(location.pathname, "payroll/process") ? " content-area--wide" : ""
          }`}
        >
          {isBlocked ? <SubscriptionExpiredScreen /> : children}
        </main>
      </div>
    </div>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
};
